import { motion } from "framer-motion";
import { Code, Briefcase, Rocket, ArrowRight } from "lucide-react";

const FoundersSection = () => {
  const founders = [
    {
      icon: Code,
      name: "Ayaan",
      role: "Technical Co-Founder",
      desc: "Builds the product. Holds 55% equity. Master of the tech stack but new to the legal stack.",
      color: "primary",
    },
    {
      icon: Briefcase,
      name: "Radha",
      role: "Business Co-Founder",
      desc: "Runs sales, partnerships, and fundraising. Navigating the messy world of Indian compliance.",
      color: "secondary",
    },
  ];

  return (
    <>
      <style>{`
        /* Prefix 'fs-' to ensure zero CSS bleeding */
        .fs-section {
          /* Locked at exactly 80px top and bottom */
          padding-top: 80px;
          padding-bottom: 80px;
          padding-left: 1rem;
          padding-right: 1rem;
          background-color: #0b1120; /* Deep Dark Theme */
          overflow: hidden;
          width: 100%;
          box-sizing: border-box;
          position: relative;
        }

        .fs-container {
          max-width: 56rem;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .fs-header {
          text-align: center;
          margin-bottom: 3.5rem;
        }

        .fs-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #2dd4bf;
          margin-bottom: 1rem;
          display: block;
        }

        .fs-title {
          font-size: 1.75rem; /* Responsive base size */
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          color: #f8fafc;
        }

        .fs-gradient {
          background: linear-gradient(to right, #2dd4bf, #22d3ee);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .fs-description {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
          max-width: 42rem;
          margin: 0 auto;
        }

        .fs-grid {
          display: grid;
          grid-template-columns: 1fr; /* Default 1 col for mobile */
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .fs-card {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 1.25rem;
          border: 1px solid rgba(255, 255, 255, 0.08);
          padding: 1.5rem;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          cursor: default;
        }

        .fs-card:hover {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(45, 212, 191, 0.4);
          transform: translateY(-8px);
        }

        .fs-icon-wrapper {
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.25rem;
        }

        .fs-bg-primary { background: rgba(45, 212, 191, 0.15); }
        .fs-bg-secondary { background: rgba(34, 211, 238, 0.15); }
        .fs-text-primary { color: #2dd4bf; }
        .fs-text-secondary { color: #22d3ee; }

        .fs-founder-name {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
          color: #f1f5f9;
        }

        .fs-founder-role {
          font-size: 0.75rem;
          font-weight: 600;
          color: #2dd4bf;
          margin: 0.25rem 0 0 0;
          text-transform: uppercase;
        }

        .fs-founder-desc {
          font-size: 0.875rem;
          color: #94a3b8;
          line-height: 1.5;
          margin-top: 1rem;
        }

        .fs-startup-card {
          position: relative;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 1.25rem;
          padding: 1.5rem;
          overflow: hidden;
          border: 1px solid rgba(45, 212, 191, 0.2);
        }

        .fs-startup-glow {
          position: absolute;
          bottom: -50px;
          right: -50px;
          width: 12rem;
          height: 12rem;
          border-radius: 50%;
          background: #2dd4bf;
          opacity: 0.1;
          filter: blur(40px);
        }

        .fs-startup-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #f8fafc;
          margin: 0;
        }

        .fs-startup-desc {
          font-size: 0.875rem;
          color: #94a3b8;
          line-height: 1.6;
          margin: 1.25rem 0;
        }

        .fs-link {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #2dd4bf;
          font-size: 0.875rem;
          font-weight: 600;
          text-decoration: none;
        }

        /* RESPONSIVE QUERIES */
        @media (min-width: 768px) {
          .fs-grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
          .fs-title { font-size: 2.5rem; }
          .fs-card { padding: 2rem; }
          .fs-icon-wrapper { width: 3.5rem; height: 3.5rem; border-radius: 1rem; }
          .fs-founder-name { font-size: 1.5rem; }
          .fs-startup-card { padding: 2.5rem; }
        }

        @media (max-width: 480px) {
          .fs-header { margin-bottom: 2.5rem; }
          .fs-title { font-size: 1.5rem; }
        }
      `}</style>

      <section className="fs-section">
        {/* Subtle background ambient light */}
        <div style={{
          position: "absolute",
          top: "10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle at center, rgba(45, 212, 191, 0.03) 0%, transparent 70%)",
          pointerEvents: "none"
        }} />

        <div className="fs-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="fs-header"
          >
            <span className="fs-label">Meet Ayaan & Radha</span>
            <h2 className="fs-title">
              Two founders. Every mistake. <br />
              <span className="fs-gradient">One definitive ebook.</span>
            </h2>
            <p className="fs-description">
              Ayaan and Radha quit their corporate jobs on a Sunday afternoon in Bengaluru. 
              Brilliant at product, but completely blind to the legal landmines ahead.
            </p>
          </motion.div>

          <div className="fs-grid">
            {founders.map((founder, i) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, x: i === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
                className="fs-card"
              >
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <motion.div
                    className={`fs-icon-wrapper ${
                      founder.color === "primary" ? "fs-bg-primary" : "fs-bg-secondary"
                    }`}
                    whileHover={{ rotate: 8, scale: 1.1 }}
                  >
                    <founder.icon
                      size={24}
                      className={founder.color === "primary" ? "fs-text-primary" : "fs-text-secondary"}
                    />
                  </motion.div>
                  <div>
                    <h3 className="fs-founder-name">{founder.name}</h3>
                    <p className="fs-founder-role">{founder.role}</p>
                  </div>
                </div>
                <p className="fs-founder-desc">{founder.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="fs-startup-card"
          >
            <div className="fs-startup-glow" />

            <div style={{ position: "relative", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <div style={{
                  width: "2.75rem",
                  height: "2.75rem",
                  borderRadius: "0.6rem",
                  background: "rgba(45, 212, 191, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <Rocket size={22} color="#2dd4bf" />
                </div>
                <div>
                  <h3 className="fs-startup-title">Their Startup: Flowy</h3>
                  <p style={{ color: "#2dd4bf", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", margin: "2px 0 0 0" }}>
                    B2B SaaS · Bengaluru
                  </p>
                </div>
              </div>
              
              <p className="fs-startup-desc">
                From a sketchbook in a HSR Layout cafe to a company with institutional investors. 
                This ebook follows their cap table, ESOP grants, and every hard-learned compliance lesson.
              </p>
              
              <a href="#get-ebook" className="fs-link">
                <span>Follow their full journey</span>
                <motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                  <ArrowRight size={16} />
                </motion.div>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default FoundersSection;